import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { AlertTriangle, Link2, Loader2 } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription } from '@/components/ui/alert';

const attachOrganizationSchema = z.object({
  organizationUuid: z
    .string()
    .trim()
    .min(1, 'Enter the organization UUID from Amplify.')
    .uuid('Enter a UUID such as 0a1b2c3d-4e5f-6789-abcd-ef0123456789. An EUID (MB_…) is not an organization UUID.'),
});

type AttachOrganizationFormValues = z.infer<typeof attachOrganizationSchema>;

interface AttachOrganizationDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  migrationName: string;
  euid: string | null;
  onSubmit: (organizationUuid: string) => void;
  isAttaching: boolean;
  /** Server-side rejection (no such org, name/owner mismatch, already attached). */
  errorMessage: string | undefined;
}

/**
 * Admin recovery for a new-organization migration whose Amplify org was created but whose
 * UUID never reached the orchestrator. Attaching records the org; Retry then runs the modules.
 */
export function AttachOrganizationDialog({
  open,
  onOpenChange,
  migrationName,
  euid,
  onSubmit,
  isAttaching,
  errorMessage,
}: AttachOrganizationDialogProps) {
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<AttachOrganizationFormValues>({
    resolver: zodResolver(attachOrganizationSchema),
    defaultValues: { organizationUuid: '' },
  });

  // Start from an empty field each time the dialog opens.
  useEffect(() => {
    if (open) reset({ organizationUuid: '' });
  }, [open, reset]);

  const fieldError = errors.organizationUuid?.message;

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!isAttaching) onOpenChange(next);
      }}
    >
      <DialogContent className="sm:max-w-[520px]">
        <form onSubmit={handleSubmit((values) => onSubmit(values.organizationUuid))} noValidate>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Link2 className="h-5 w-5" aria-hidden="true" />
              Attach Amplify Organization
            </DialogTitle>
            <DialogDescription>
              Use this when Amplify created the organization for{' '}
              <span className="font-medium text-foreground">{migrationName}</span> but its UUID
              never reached the orchestrator — the migration log says &ldquo;Do not retry yet&rdquo;.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <ul className="list-disc list-inside space-y-1 text-sm text-muted-foreground">
              <li>
                Find the organization in Amplify
                {euid ? (
                  <>
                    {' '}by EUID <span className="font-mono text-foreground">{euid}</span>
                  </>
                ) : null}
                .
              </li>
              <li>Its name and owner email must match this migration, or the attach is rejected.</li>
              <li>Attaching runs nothing. Retry the migration afterwards to run its modules.</li>
            </ul>

            <div className="space-y-2">
              <Label htmlFor="attach-organization-uuid">Amplify organization UUID</Label>
              <Input
                id="attach-organization-uuid"
                placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx"
                autoComplete="off"
                spellCheck={false}
                className="font-mono"
                readOnly={isAttaching}
                aria-invalid={fieldError ? true : undefined}
                aria-describedby={fieldError ? 'attach-organization-uuid-error' : undefined}
                {...register('organizationUuid')}
              />
              {fieldError && (
                <p id="attach-organization-uuid-error" className="text-sm text-destructive">
                  {fieldError}
                </p>
              )}
            </div>

            {errorMessage && (
              <Alert variant="destructive">
                <AlertTriangle className="h-4 w-4" />
                <AlertDescription>{errorMessage}</AlertDescription>
              </Alert>
            )}
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={isAttaching}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={isAttaching}>
              {isAttaching && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Attach Organization
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
